import React, { useState, useEffect } from 'react';
import { Button } from '../../atoms/Button/Button';
import { Note } from '../../../types/Note';
import { noteService } from '../../../services/noteService';
import MarkdownEditor from '../../atoms/MarkdownEditor/MarkdownEditor';
import './NoteForm.css';

interface NoteFormProps {
  userId: string;
  onNoteCreated?: () => void;
  initialNote?: Note | null;
}

export const NoteForm: React.FC<NoteFormProps> = ({ userId, onNoteCreated, initialNote }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [currentTag, setCurrentTag] = useState('');
  const [pinned, setPinned] = useState(false);
  const [archived, setArchived] = useState(false);
  const [timelineDate, setTimelineDate] = useState<Date>(new Date());

  useEffect(() => {
    if (initialNote) {
      setTitle(initialNote.title);
      setContent(initialNote.content);
      setTags(initialNote.tags);
      setPinned(initialNote.pinned);
      setArchived(initialNote.archived);
      setTimelineDate(new Date(initialNote.timelineDate));
    }
  }, [initialNote]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const note: Note = {
      ...(initialNote && initialNote.id ? { id: initialNote.id } : {}),
      title,
      content,
      tags,
      pinned,
      archived,
      timelineDate,
      userId,
      createdAt: initialNote?.createdAt || new Date(),
      updatedAt: new Date(),
      backlinks: initialNote?.backlinks || JSON.stringify([])
    };

    try {
      if (initialNote && initialNote.id) {
        await noteService.updateNote(initialNote.id, note);
      } else {
        await noteService.createNote(note);
      }
      setTitle('');
      setContent('');
      setTags([]);
      setPinned(false);
      setArchived(false);
      setTimelineDate(new Date());
      onNoteCreated?.();
    } catch (error) {
      console.error('Error creating/updating note:', error);
    }
  };

  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && currentTag.trim()) {
      e.preventDefault();
      if (!tags.includes(currentTag.trim())) {
        setTags([...tags, currentTag.trim()]);
      }
      setCurrentTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  return (
    <form className="note-form" onSubmit={handleSubmit}>
      <input
        type="text"
        className="note-form__input"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Note title"
        required
      />
      <MarkdownEditor onMarkdownChange={setContent} value={content} />
      <input
        type="text"
        className="note-form__tags-input"
        value={currentTag}
        onChange={(e) => setCurrentTag(e.target.value)}
        onKeyDown={handleAddTag}
        placeholder="Add tags (press Enter to add)"
      />
      <div className="note-form__tags-container">
        {tags.map((tag) => (
          <span key={tag} className="note-form__tag">
            {tag}
            <button
              type="button"
              className="note-form__remove-tag"
              onClick={() => handleRemoveTag(tag)}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="note-form__checkbox-container">
        <label className="note-form__checkbox-label">
          <input
            type="checkbox"
            checked={pinned}
            onChange={(e) => setPinned(e.target.checked)}
          />
          Pin Note
        </label>
        <label className="note-form__checkbox-label">
          <input
            type="checkbox"
            checked={archived}
            onChange={(e) => setArchived(e.target.checked)}
          />
          Archive Note
        </label>
      </div>
      <input
        type="datetime-local"
        className="note-form__input"
        value={timelineDate instanceof Date && !isNaN(timelineDate as any) ? timelineDate.toISOString().slice(0, 16) : ''}
        onChange={(e) => setTimelineDate(new Date(e.target.value))}
      />
      <Button type="submit">{initialNote ? 'Update Note' : 'Create Note'}</Button>
    </form>
  );
}; 